import React, { useEffect, useState, useMemo } from 'react';
import {
	Box,
	MenuList,
	MenuItem,
	ListItemText,
	Divider,
	Button,
	Checkbox
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { _isEqual, deepClone } from 'helper/helper';
import MTSCustomRangeInput from './MTSCustomRange';
import MTSStyle, { CustomizedTooltip } from './MTSStyle';
import { setSelectedList } from '../Preview.actions';
import { infoText, MTS_MENU, getMenuOptions } from './MTSUtils';
import {
	CLEAR_ALL,
	CANCEL,
	OKAY,
	MULTI_TIME_SELECTION,
	MULTI_TIME_PIVOT_TEXT,
	MTS_SELECT_UPTO_SIX,
	WALMART_CALENDAR,
	CUSTOM_DATE_1,
	CUSTOM_DATE_2
} from 'constants/constant';

const MAX_OPTIONS = 6;
const initialSelection = MTS_MENU.DAY;

export const initialState = {
	start: undefined,
	end: undefined,
	week1: undefined,
	week2: undefined
};

/**
 * MultiTimeSelectionWidget
 * This widget opens when the user clicks on Multi-time Filter from Edit filter Panel
 * Using this widget the user will select Fuzzy time, Day, Week, Quarter and Custom filters
 */
const MultiTimeSelectionWidget = ({ mtsState, templateId, open, onClose, onMTSUpdate }) => {
	const classes = MTSStyle();
	const dispatch = useDispatch();
	const [optionsSelected, setOptionsSelected] = useState([]);
	const [selectedMenu, setSelectedMenu] = useState(initialSelection);

	const filterList = useSelector((state) => state.previewReducer.filterList);
	const selectedList = useSelector((state) => state.previewReducer.selectedList);
	const templateData = useSelector((state) => state.templateReducer.templateData);

	// State for Date/Week calendars
	const [rangeTypeOne, setRangeTypeOne] = useState(() => {
		const customDateOne = mtsState?.[CUSTOM_DATE_1];
		if (customDateOne) {
			return customDateOne.rangeType;
		}
		return '';
	});
	const [rangeTypeTwo, setRangeTypeTwo] = useState(() => {
		const customDateTwo = mtsState?.[CUSTOM_DATE_2];
		if (customDateTwo) {
			return customDateTwo.rangeType;
		}
		return '';
	});

	const [dateRangeOne, setDateRangeOne] = useState(() => {
		const customDateOne = mtsState?.[CUSTOM_DATE_1];
		if (customDateOne) {
			return customDateOne.date;
		}
		return initialState;
	});
	const [dateRangeTwo, setDateRangeTwo] = useState(() => {
		const customDateTwo = mtsState?.[CUSTOM_DATE_2];
		if (customDateTwo) {
			return customDateTwo.date;
		}
		return initialState;
	});

	const [calendarAnchorElOne, setCalendarAnchorElOne] = useState(null);
	const [calendarAnchorElTwo, setCalendarAnchorElTwo] = useState(null);

	/**
	 * Menu & Options corresponding to the templateId
	 */
	const menuWithOptions = getMenuOptions(templateId);
	const currentOptions = menuWithOptions[selectedMenu] || [];

	const numberOfOptionsSelected = optionsSelected.length;

	const disableActionBtn = numberOfOptionsSelected === 0;
	const isCustomMenuSelected = selectedMenu === MTS_MENU.CUSTOM;
	const shouldDisableOptions = numberOfOptionsSelected >= MAX_OPTIONS;

	const disableCalendarOne = useMemo(() => {
		const noValueSet = _isEqual(initialState, dateRangeOne);
		return noValueSet && shouldDisableOptions;
	}, [shouldDisableOptions, dateRangeOne]);

	const disableCalendarTwo = useMemo(() => {
		const noValueSet = _isEqual(initialState, dateRangeTwo);
		return noValueSet && shouldDisableOptions;
	}, [shouldDisableOptions, dateRangeTwo]);

	const onSubMenuClick = (option) => {
		if (option !== selectedMenu) {
			setSelectedMenu(option);
		}
	};

	const numberOfCustomOptionsSelected = optionsSelected.reduce((accumulator, currentValue) => {
		if ([CUSTOM_DATE_1, CUSTOM_DATE_2].includes(currentValue)) {
			return accumulator + 1;
		}
		return accumulator;
	}, 0);

	useEffect(() => {
		setOptionsSelected(Object.keys(mtsState));
	}, [mtsState]);

	const onOptionCheck = (option) => {
		if (optionsSelected.includes(option)) {
			setOptionsSelected(optionsSelected.filter((currentOption) => currentOption !== option));
		}
		// Only add if the number of options is less than 6 (MAX_OPTIONS)
		else if (numberOfOptionsSelected < MAX_OPTIONS) {
			setOptionsSelected([...optionsSelected, option]);
			return [...currentOptions, option];
		}
	};

	const onOptionAdd = (option) => {
		if (!optionsSelected.includes(option)) {
			setOptionsSelected([...optionsSelected, option]);
		}
	};

	const onOptionRemove = (option) => {
		if (optionsSelected.includes(option)) {
			setOptionsSelected(optionsSelected.filter((currentOption) => currentOption !== option));
		}
	};

	const onSubmit = () => {
		// update MTS state locally
		const finalMTSState = optionsSelected.reduce((accumulator, currentOption) => {
			if (currentOption === CUSTOM_DATE_1) {
				return {
					...accumulator,
					[CUSTOM_DATE_1]: { date: { ...dateRangeOne }, rangeType: rangeTypeOne }
				};
			}
			if (currentOption === CUSTOM_DATE_2) {
				return {
					...accumulator,
					[CUSTOM_DATE_2]: { date: { ...dateRangeTwo }, rangeType: rangeTypeTwo }
				};
			}
			return { ...accumulator, [currentOption]: currentOption };
		}, {});

		onMTSUpdate(finalMTSState);

		// Add mts as a column if it is not already present in redux
		const isMTSPresentInColumns =
			selectedList?.findIndex(
				(selectedColumn) => selectedColumn?.localName === MULTI_TIME_SELECTION
			) > -1;

		if (!isMTSPresentInColumns) {
			const parent = WALMART_CALENDAR;
			let _subAtt = {};

			const mtsFilter = filterList?.find((filter) => filter?.localName === MULTI_TIME_SELECTION);

			const _template = templateData.find((t) => Number(t.templateId) === templateId);
			const _o = _template?.time.find((_t) => _t.businessName === parent);
			_subAtt = _o?.subAttributes.find((s) => s.businessName === 'Week');

			const newMTSFilter = {
				...mtsFilter,
				..._subAtt
			};

			delete newMTSFilter?.mts;

			dispatch(setSelectedList(deepClone([...selectedList, newMTSFilter])));
		}
		onClose();
	};

	if (!open) {
		return null;
	}

	/**
	 * Calculate selected option count for each Menu
	 */
	const menuWithOptionCount = {
		[MTS_MENU.DAY]: 0,
		[MTS_MENU.WEEK]: 0,
		[MTS_MENU.FUZZY_TIME]: 0,
		[MTS_MENU.CUSTOM]: 0
	};
	for (const menu in menuWithOptions) {
		if (Object.hasOwnProperty.call(menuWithOptions, menu)) {
			const options = menuWithOptions[menu];
			options.forEach(({ value }) => {
				if (optionsSelected.includes(value)) {
					menuWithOptionCount[menu] = menuWithOptionCount[menu] + 1;
				}
			});
		}
	}

	const handleCalendarAnchorElOne = (target) => {
		setCalendarAnchorElOne(target);
		if (target !== null) {
			setCalendarAnchorElTwo(null);
		}
	};

	const handleCalendarAnchorElTwo = (target) => {
		setCalendarAnchorElTwo(target);
		if (target !== null) {
			setCalendarAnchorElOne(null);
		}
	};

	const clearAll = () => {
		setOptionsSelected([]);
		setDateRangeOne(initialState);
		setDateRangeTwo(initialState);
		setRangeTypeOne('');
		setRangeTypeTwo('');
	};

	return (
		<Box className={classes.MTSWidgetContainer}>
			<Box className={classes.MTSWidgetBody}>
				{/* Side Panel */}
				<Box className={classes.MTSWidgetSidePanel}>
					<Box>
						<MenuList>
							{Object.keys(menuWithOptions).map((menu, index) => {
								const isSelected = selectedMenu === menu;
								return (
									<MenuItem
										id={`left-submenu-${menu}`}
										key={index}
										className={
											isSelected
												? classes.MTSWidgetSidePanelItemSelected
												: classes.MTSWidgetSidePanelItem
										}
										onClick={() => onSubMenuClick(menu)}>
										<ListItemText className={classes.MTSWidgetSidePanelText}>
											{menu} {menuWithOptionCount[menu] > 0 ? `(${menuWithOptionCount[menu]})` : ''}
										</ListItemText>
									</MenuItem>
								);
							})}
						</MenuList>
					</Box>
					<Divider />
					<Box marginTop={'8px'}>
						<MenuItem
							id="left-submenu-custom"
							onClick={() => onSubMenuClick(MTS_MENU.CUSTOM)}
							className={
								isCustomMenuSelected
									? classes.MTSWidgetSidePanelItemSelected
									: classes.MTSWidgetSidePanelItem
							}>
							<ListItemText className={classes.MTSWidgetSidePanelText}>
								{MTS_MENU.CUSTOM} ({numberOfCustomOptionsSelected}/2)
							</ListItemText>
							<CustomizedTooltip title={infoText} arrow placement="top-start">
								<InfoOutlinedIcon
									fontSize="small"
									style={{ color: '#74767C', marginLeft: '4px', width: 16 }}
								/>
							</CustomizedTooltip>
						</MenuItem>
					</Box>
				</Box>
				<Divider className={classes.verticalDivider} orientation="vertical" flexItem={true} />

				{/* Main Selection Area */}
				<Box className={classes.MTSWidgetMainPanel}>
					{isCustomMenuSelected ? (
						<Box className={classes.MTSCustomContainer}>
							<MTSCustomRangeInput
								calendarAnchorEl={calendarAnchorElOne}
								setCalendarAnchorEl={handleCalendarAnchorElOne}
								dateRange={dateRangeOne}
								setDateRange={(selection) => setDateRangeOne(selection)}
								isWeekRange={rangeTypeOne === 'week'}
								isDateRange={rangeTypeOne === 'day'}
								onOptionAdd={() => onOptionAdd(CUSTOM_DATE_1)}
								onOptionRemove={() => onOptionRemove(CUSTOM_DATE_1)}
								setRangeType={(type) => setRangeTypeOne(type)}
								disabled={disableCalendarOne}
								isFirstCustomRange={true}
							/>
							<MTSCustomRangeInput
								calendarAnchorEl={calendarAnchorElTwo}
								setCalendarAnchorEl={handleCalendarAnchorElTwo}
								dateRange={dateRangeTwo}
								setDateRange={(selection) => setDateRangeTwo(selection)}
								isWeekRange={rangeTypeTwo === 'week'}
								isDateRange={rangeTypeTwo === 'day'}
								onOptionAdd={() => onOptionAdd(CUSTOM_DATE_2)}
								onOptionRemove={() => onOptionRemove(CUSTOM_DATE_2)}
								setRangeType={(type) => setRangeTypeTwo(type)}
								disabled={disableCalendarTwo}
							/>
						</Box>
					) : (
						currentOptions.map((option, index) => {
							const isChecked = optionsSelected.includes(option.value);
							const shouldDisableOption = !isChecked && shouldDisableOptions;
							return (
								<MenuItem
									key={index}
									name={option.value}
									value={option.value}
									className={classes.subMenuItem}
									onClick={() => onOptionCheck(option.value)}>
									<Checkbox
										size="medium"
										color="primary"
										checked={isChecked}
										disabled={shouldDisableOption}
										className={
											shouldDisableOption
												? classes.subMenuItemDisabledCheckBox
												: classes.subMenuItemCheckBox
										}
									/>
									<ListItemText primary={option.label} />
								</MenuItem>
							);
						})
					)}
				</Box>
			</Box>
			<Divider />

			{/* Footer */}
			<Box className={classes.MTSWidgetFooter}>
				<Box className={classes.MTSWidgetInfo}>
					{`${MTS_SELECT_UPTO_SIX} (${numberOfOptionsSelected}/6). ${MULTI_TIME_PIVOT_TEXT}`}
				</Box>
				<Divider />
				{/* Action Buttons */}
				<Box className={classes.MTSWidgetAction}>
					<Box>
						<Button
							id="multi-time-selection-widget-clear-all"
							disableTouchRipple
							disabled={disableActionBtn}
							onClick={clearAll}
							className={disableActionBtn ? classes.clearAllDisabledBtn : classes.clearAllBtn}>
							{CLEAR_ALL}
						</Button>
					</Box>
					<Box className={classes.MTSActionRight}>
						<Button
							id="multi-time-selection-widget-cancel"
							onClick={onClose}
							disableTouchRipple
							className={`${classes.MTSActionBtn} ${classes.MTSSecondaryBtn}`}>
							{CANCEL}
						</Button>
						<Button
							id="multi-time-selection-widget-okay"
							disableTouchRipple
							// TODO: if the options previously selected is equal to current options, then disable
							// disabled={optionsSelected?.length === 0}
							onClick={onSubmit}
							className={`${classes.MTSActionBtn} ${classes.MTSPrimaryBtn}`}>
							{OKAY}
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default MultiTimeSelectionWidget;
